import {Box, Transition} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import type React from 'react';
import {useState} from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {REPORT_ANALYTICS_OGP_IMAGE} from '../../../config/constants/img';
import type {ReportSummaryType} from '../../../core/entities/reportAnalytics/schema';
import {useGraduationYearsHot} from '../../../core/repositories/recruitingInfoRepository';
import {useUsersMe} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {useAuth} from '../../../core/usecases/useAuth';
import {useLoading} from '../../../core/usecases/useLoading';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {useHandleReportDepartments} from '../usecase/useHandleReportDepartment';
import {useParseSummaries} from '../usecase/useParseSummaries';
import {AnalyticsSidebar} from './Presenter/AnaliticsSidebar';
import {LoadingBox} from './Presenter/LoadingBox';
import {NoDataBox} from './Presenter/NoDataBox';
import {OutlineSidebar} from './Presenter/OutlineSidebar';
import {RecommendRecrutments} from './Presenter/RecommendRecruitments';
import {ReferenceBox} from './Presenter/ReferenceBox';
import {ResultHeadBox} from './Presenter/ResultHeadBox';
import {ResultSummaryBox} from './Presenter/ResultSummaryBox';

type Props = {
  reportSummaryData?: ReportSummaryType;
};

export const ReportAnalyticsResultPage: React.FC<Props> = ({
  reportSummaryData,
}) => {
  const {isSignedIn} = useAuth();
  const router = useRouter();
  const {isPC} = useMediaQuery();
  const {isLoading} = useLoading(2000);
  const {isLoading: isMounting} = useLoading(2500);
  const {companyID} = router.query as {companyID: string};
  const {departmentID} = router.query as {departmentID: string};
  const {reportType} = router.query as {reportType: string};

  const {result, companyName, reference} = useParseSummaries(reportSummaryData);

  const {departments} = useHandleReportDepartments({
    reportType,
    companyID: Number.parseInt(companyID, 10),
    departmentID: Number.parseInt(departmentID, 10),
  });
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const TITLE = 'AI選考対策 分析結果';
  const departmentName = reportSummaryData?.departmentName;

  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);
  const [hasRecruitments, setHasRecruitments] = useState<boolean>(false);
  const {data: graduationYearsHots} = useGraduationYearsHot({});
  const {data: usersMe} = useUsersMe({
    config: {enabled: isValid},
  });

  // 卒年が指定されていない&ログイン時&ユーザーの卒年がターゲット卒年に含まれている場合、デフォルトで卒年をセット
  const graduationYear =
    usersMe?.ok &&
    graduationYearsHots?.ok &&
    graduationYearsHots.value.years.includes(usersMe.value.graduationYear)
      ? usersMe.value.graduationYear
      : undefined;

  return (
    <>
      <Head>
        <title key="title">
          {TITLE} {` | ${companyName} | ${departmentName}`}
          {TITLE_SUFFIX}
        </title>
        <meta
          key="og:title"
          property="og:title"
          content={`${TITLE} | ${companyName} | ${departmentName} ${TITLE_SUFFIX}`}
        />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={`${TITLE} | ${companyName} | ${departmentName} ${TITLE_SUFFIX}`}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={`${GSSKT_HOST}${REPORT_ANALYTICS_OGP_IMAGE}`}
        />
        <meta
          key="description"
          name="description"
          content={`${companyName} ${departmentName}のAI選考対策分析結果です。膨大な選考体験記データから抽出した選考フロー、実際の質問例、選考突破のためのアドバイスをご確認いただけます。効率的な選考対策にお役立てください!`}
        />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,ES・体験記,投資銀行,コンサル,商社"
        />
        <meta
          key="og:description"
          property="og:description"
          content={`${companyName} ${departmentName}のAI選考対策分析結果です。膨大な選考体験記データから抽出した選考フロー、実際の質問例、選考突破のためのアドバイスをご確認いただけます。効率的な選考対策にお役立てください!`}
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={`${TITLE} | ${companyName} | ${departmentName} ${TITLE_SUFFIX}`}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={REPORT_ANALYTICS_OGP_IMAGE}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={`${companyName} ${departmentName}のAI選考対策分析結果です。膨大な選考体験記データから抽出した選考フロー、実際の質問例、選考突破のためのアドバイスをご確認いただけます。効率的な選考対策にお役立てください!`}
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>
      {isLoading ? (
        <>
          {/* SEO対策のためにクローラーにHeadBoxを読み取らせる */}
          <Box className="hidden">
            <ResultHeadBox
              reportSummaryData={reportSummaryData}
              currentUrl={currentUrl}
              isPC={isPC}
              reportType={reportType}
            />
          </Box>
          <LoadingBox />
        </>
      ) : (
        <Transition
          mounted={!isMounting}
          transition="fade"
          duration={1000}
          timingFunction="ease"
        >
          {(styles) =>
            result.length > 0 ? (
              <Box
                className={
                  'mx-auto flex pc:w-[1200px] w-full justify-between pc:mt-0 mt-[-12px]'
                }
                style={styles}
              >
                <Box className="pc:w-[880px] w-full">
                  <ResultHeadBox
                    reportSummaryData={reportSummaryData}
                    currentUrl={currentUrl}
                    isPC={isPC}
                    reportType={reportType}
                  />
                  <ResultSummaryBox
                    isSignedIn={isSignedIn}
                    isPC={isPC}
                    summaries={result}
                    reportType={reportType}
                  />
                  {!isPC && (
                    <Box className="w-full">
                      <AnalyticsSidebar
                        router={router}
                        companyID={Number.parseInt(companyID, 10)}
                        departments={departments || []}
                      />
                    </Box>
                  )}
                  <ReferenceBox
                    companyId={Number.parseInt(companyID, 10)}
                    referenceIds={reference}
                    reportType={reportType}
                    isPC={isPC}
                  />
                  <RecommendRecrutments
                    graduationYear={graduationYear}
                    industryCategoryID={
                      reportSummaryData
                        ? reportSummaryData.industryCategoryID
                        : 0
                    }
                    setHasRecruitments={setHasRecruitments}
                    titleStyleGradient={true}
                  />
                </Box>
                {isPC && (
                  <Box className="w-[320px] pl-16">
                    <Box className="sticky top-[150px]">
                      <OutlineSidebar
                        summaries={result}
                        reportType={reportType}
                        hasRecruitments={hasRecruitments}
                      />
                      <AnalyticsSidebar
                        router={router}
                        companyID={Number.parseInt(companyID, 10)}
                        departments={departments || []}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <NoDataBox />
            )
          }
        </Transition>
      )}
    </>
  );
};
